import { useContext, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import DataContext from "../../../context/DataContext";
import { useRequest } from "../../../hooks/useRequest";
import { useToolsRunTrackedWells } from "../../../hooks/useToolsRunTrackedWells";
import { COILineItemProps } from "../../../interfaces/interfaces";

type NamedOption = { id: number; name: string };
type ProductOption = NamedOption & { partNumber: string };
type FreeOption<T> = T | string | null;

interface Props {
  row: COILineItemProps | null;
  onClose: () => void;
  onResolved: () => void;
}

const idFor = <T extends NamedOption>(value: FreeOption<T>) =>
  typeof value === "object" && value ? value.id : undefined;
const createFor = <T extends NamedOption>(value: FreeOption<T>) =>
  typeof value === "string" && value.trim()
    ? { name: value.trim() }
    : undefined;

export const COIResolveDialog = ({ row, onClose, onResolved }: Props) => {
  const { handleRequest } = useRequest();
  const { productOptions } = useContext(DataContext);
  const { wellOptions, fetchDataWells } = useToolsRunTrackedWells();
  const products = productOptions as ProductOption[];
  const wells = useMemo<NamedOption[]>(
    () =>
      wellOptions
        .filter((option) => option.id != null)
        .map((option) => ({ id: option.id as number, name: option.name })),
    [wellOptions],
  );
  const [well, setWell] = useState<FreeOption<NamedOption>>(null);
  const [product, setProduct] = useState<FreeOption<ProductOption>>(null);
  const [wellInput, setWellInput] = useState("");
  const [productInput, setProductInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!row) return;
    fetchDataWells(row.clientId);
    setWell(row.well || null);
    const selectedProduct =
      products.find((option) => option.id === row.productId) || null;
    setProduct(selectedProduct);
    setProductInput(
      selectedProduct
        ? `${selectedProduct.partNumber} · ${selectedProduct.name}`
        : "",
    );
    setWellInput(row.well?.name || row.fromWellRaw || "");
    setError("");
    // Product master-data actions are stable for the lifetime of this dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.id]);

  useEffect(() => {
    if (!row || well || !row.wellId) return;
    const selectedWell = wells.find((option) => option.id === row.wellId);
    if (selectedWell) setWell(selectedWell);
  }, [row, well, wells]);

  const effectiveWell = well || (wellInput.trim() ? wellInput.trim() : null);
  const effectiveProduct =
    product || (productInput.trim() ? productInput.trim() : null);

  const submit = async () => {
    if (!row || !effectiveProduct) return;
    setBusy(true);
    setError("");
    try {
      const response = await handleRequest({
        endpoint: `coi/${row.id}/resolve`,
        options: {
          method: "PUT",
          body: JSON.stringify({
            wellId: idFor(effectiveWell),
            productId: idFor(effectiveProduct),
            createWell: createFor(effectiveWell),
            createProduct: createFor(effectiveProduct),
          }),
        },
      });
      if (!response.success) {
        throw new Error(response.error || "The COI row could not be resolved");
      }
      onResolved();
      onClose();
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "The COI row could not be resolved",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={Boolean(row)}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ fontWeight: 850 }}>Resolve COI match</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            p: 2,
            mb: 2,
            bgcolor: "#F7F6F1",
            border: "1px solid #E9E6DB",
            borderRadius: 2,
          }}
        >
          <Typography variant="overline" color="text.secondary" fontWeight={800}>
            Source row
          </Typography>
          <Typography fontWeight={800}>{row?.partNumber || "—"}</Typography>
          <Typography variant="body2" color="text.secondary">
            {row?.equipmentDescription || "No equipment description"} · From{" "}
            {row?.fromWellRaw || "—"}
          </Typography>
        </Box>
        <Autocomplete
          freeSolo
          selectOnFocus
          clearOnBlur={false}
          value={well}
          options={wells}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.name
          }
          onChange={(_, value) => {
            setWell(value);
            if (typeof value === "string") setWellInput(value);
          }}
          onInputChange={(_, value, reason) => {
            setWellInput(value);
            if (reason === "input") setWell(null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="From well"
              margin="normal"
              helperText="Select an existing tracked well or type a new name."
            />
          )}
        />
        <Autocomplete
          freeSolo
          selectOnFocus
          clearOnBlur={false}
          value={product}
          inputValue={productInput}
          options={products}
          getOptionLabel={(option) =>
            typeof option === "string"
              ? option
              : `${option.partNumber} · ${option.name}`
          }
          onChange={(_, value) => {
            setProduct(value);
            if (typeof value === "string") setProductInput(value);
          }}
          onInputChange={(_, value, reason) => {
            setProductInput(value);
            if (reason === "input") setProduct(null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Product"
              margin="normal"
              helperText={`Source P/N: ${row?.partNumber || "—"}`}
            />
          )}
        />
        {row?.equipmentDescription && !idFor(effectiveProduct) ? (
          <Button
            size="small"
            onClick={() => {
              setProduct(null);
              setProductInput(row.equipmentDescription as string);
            }}
            sx={{ mt: -1, textTransform: "none", fontWeight: 700 }}
          >
            + Create product from workbook description
          </Button>
        ) : null}
        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || !effectiveProduct}
          onClick={submit}
          startIcon={<AutoFixHighRoundedIcon />}
        >
          Save match
        </Button>
      </DialogActions>
    </Dialog>
  );
};
